import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { LAB_TESTS_SETTINGS, LAB_TEST_ADMIN } from '@/constants/testIds';
import { Plus, DotsThreeVertical, PencilSimple, Trash, TestTube, ShieldStar } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useAuth } from '@/context/AuthContext';

function LabTestForm({ initial, onSaved, isAdmin, concepts }) {
    const [f, setF] = useState({
        test_name: initial?.test_name || '',
        unit: initial?.unit || '',
        ref_low: initial?.ref_low ?? '',
        ref_high: initial?.ref_high ?? '',
        ref_text: initial?.ref_text || '',
        category: initial?.category || '',
        concept_key: initial?.concept_key || 'none',
    });
    const save = async () => {
        if (!f.test_name.trim()) return;
        const payload = {
            test_name: f.test_name.trim(),
            unit: f.unit || null,
            ref_low: f.ref_low === '' ? null : parseFloat(f.ref_low),
            ref_high: f.ref_high === '' ? null : parseFloat(f.ref_high),
            ref_text: f.ref_text || null,
            category: f.category || null,
            concept_key: f.concept_key && f.concept_key !== 'none' ? f.concept_key : null,
        };
        try {
            if (initial) await api.patch(`/lab-tests/${initial.lab_test_id}`, payload);
            else await api.post('/lab-tests', payload);
            toast.success('Saved'); onSaved?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <>
            <div className="space-y-3">
                <div><Label>Test name</Label><Input data-testid={LAB_TESTS_SETTINGS.nameInput} value={f.test_name} onChange={e => setF({ ...f, test_name: e.target.value })} /></div>
                <div className="grid grid-cols-3 gap-2">
                    <div><Label>Unit</Label><Input data-testid={LAB_TESTS_SETTINGS.unitInput} value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} placeholder="mg/dL" /></div>
                    <div><Label>Normal low</Label><Input data-testid={LAB_TESTS_SETTINGS.lowInput} type="number" step="0.01" value={f.ref_low} onChange={e => setF({ ...f, ref_low: e.target.value })} /></div>
                    <div><Label>Normal high</Label><Input data-testid={LAB_TESTS_SETTINGS.highInput} type="number" step="0.01" value={f.ref_high} onChange={e => setF({ ...f, ref_high: e.target.value })} /></div>
                </div>
                <div><Label>Range as text (optional)</Label><Input data-testid={LAB_TESTS_SETTINGS.textInput} value={f.ref_text} onChange={e => setF({ ...f, ref_text: e.target.value })} placeholder='"< 200"' /></div>
                <div><Label>Category</Label><Input value={f.category} onChange={e => setF({ ...f, category: e.target.value })} placeholder="Lipids, Diabetes…" /></div>
                {isAdmin && (
                    <div className="pt-2 mt-2 border-t border-slate-200">
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-primary mb-1">
                            <ShieldStar size={11} weight="duotone" /> Admin only
                        </div>
                        <Label>Linked concept</Label>
                        <Select value={f.concept_key || 'none'} onValueChange={v => setF({ ...f, concept_key: v })}>
                            <SelectTrigger data-testid={LAB_TEST_ADMIN.conceptSelect}><SelectValue placeholder="No concept linked" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No concept linked</SelectItem>
                                {(concepts || []).filter(c => c.status === 'Active').map(c => (
                                    <SelectItem key={c.key} value={c.key}>
                                        {c.display_name}
                                        <span className="text-slate-400"> · {c.key}</span>
                                        {c.code && <span className="text-slate-400"> · {c.code_system} {c.code}</span>}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <div className="text-[11px] text-slate-500 mt-1">Optional. Links this lab test to a dictionary concept for structured export. Coordinators never see this.</div>
                    </div>
                )}
            </div>
            <DialogFooter><Button data-testid={LAB_TESTS_SETTINGS.submit} onClick={save} className="rounded-full bg-primary text-primary-foreground">{initial ? 'Save' : 'Add test'}</Button></DialogFooter>
        </>
    );
}

export default function LabTestsSettings() {
    const qc = useQueryClient();
    const { isAdmin, canMutateAdmin } = useAuth();
    const { data: tests = [] } = useQuery({ queryKey: ['lab-tests'], queryFn: async () => (await api.get('/lab-tests')).data });
    const { data: concepts = [] } = useQuery({
        queryKey: ['admin-concepts'],
        queryFn: async () => (await api.get('/admin/concepts')).data,
        enabled: true,
    });
    const conceptMap = React.useMemo(() => Object.fromEntries((concepts || []).map(c => [c.key, c])), [concepts]);
    const [addOpen, setAddOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [del, setDel] = useState(null);

    const invalidate = () => qc.invalidateQueries({ queryKey: ['lab-tests'] });
    const doDelete = async () => {
        try {
            await api.delete(`/lab-tests/${del.lab_test_id}`);
            toast.success('Deleted');
            setDel(null); invalidate();
        } catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={LAB_TESTS_SETTINGS.root} className="px-6 md:px-10 py-8 max-w-4xl">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Settings</div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Lab tests</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">A shared list you pick from when adding a lab result — keeps the unit and normal range consistent across every family member.</p>
                </div>
                {canMutateAdmin && (
                    <Dialog open={addOpen} onOpenChange={setAddOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid={LAB_TESTS_SETTINGS.addButton} className="rounded-full bg-primary text-primary-foreground"><Plus size={16} className="mr-1" /> Add test</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-heading text-2xl">Add lab test</DialogTitle></DialogHeader>
                            <LabTestForm onSaved={() => { setAddOpen(false); invalidate(); }} isAdmin={isAdmin} concepts={concepts} />
                        </DialogContent>
                    </Dialog>
                )}
            </header>

            {tests.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center">
                    <TestTube size={40} className="mx-auto text-slate-400" />
                    <div className="font-heading text-2xl mt-3 text-slate-800">No lab tests yet</div>
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-card shadow-sm divide-y divide-slate-200">
                    {tests.map(t => (
                        <div key={t.lab_test_id} data-testid={LAB_TESTS_SETTINGS.row} className="flex items-center gap-3 px-5 py-3">
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-900">{t.test_name}</div>
                                <div className="text-xs text-slate-500">
                                    {t.unit && `${t.unit} · `}
                                    {t.ref_text || (t.ref_low != null && t.ref_high != null ? `${t.ref_low}–${t.ref_high}` :
                                        t.ref_high != null ? `< ${t.ref_high}` : t.ref_low != null ? `> ${t.ref_low}` : 'no range')}
                                    {t.category && ` · ${t.category}`}
                                </div>
                                {(isAdmin || t.concept_key) && (
                                    <div className="text-[11px] mt-1 flex items-center gap-1.5 text-primary/80">
                                        <ShieldStar size={11} weight="duotone" />
                                        {t.concept_key && conceptMap[t.concept_key] ? (
                                            <>
                                                <span className="font-mono">{t.concept_key}</span>
                                                {conceptMap[t.concept_key].code && (
                                                    <span className="text-slate-500 font-mono">· {conceptMap[t.concept_key].code_system} {conceptMap[t.concept_key].code}</span>
                                                )}
                                            </>
                                        ) : (
                                            <span className="text-slate-400 italic">No concept linked</span>
                                        )}
                                    </div>
                                )}
                            </div>
                            {canMutateAdmin && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><DotsThreeVertical size={18} weight="bold" /></button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem data-testid={LAB_TESTS_SETTINGS.edit} onClick={() => setEdit(t)}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                        <DropdownMenuItem data-testid={LAB_TESTS_SETTINGS.delete} onClick={() => setDel(t)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit lab test</DialogTitle></DialogHeader>
                    {edit && <LabTestForm initial={edit} onSaved={() => { setEdit(null); invalidate(); }} isAdmin={isAdmin} concepts={concepts} />}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!del} onOpenChange={(v) => !v && setDel(null)}
                title="Delete this lab test?"
                description={del ? `"${del.test_name}" won't appear in the picker anymore. Existing results are unaffected.` : ''}
                onConfirm={doDelete}
            />
        </div>
    );
}
